import Link, { type LinkProps } from "next/link";
import { cva, type VariantProps } from "class-variance-authority";
import { isTruthy } from "@remi/ts-utils";

import { cn } from "../lib/utils";

const shallowLinkVariants = cva(
  "flex flex-row items-center gap-1 text-nowrap rounded-sm font-medium ring-offset-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-border-accent focus-visible:ring-offset-2",
  {
    variants: {
      variant: {
        accent: "text-text-accent disabled:text-text-accentDisabled",
        primary: "text-text-primary",
        secondary: "text-text-tertiary",
        muted: "text-text-secondary",
      },
      size: {
        small: "text-xs",
        medium: "text-sm",
        large: "text-base",
      },
    },
    defaultVariants: {
      variant: "accent",
      size: "small",
    },
  },
);

interface RemiLinkProps extends LinkProps, VariantProps<typeof shallowLinkVariants> {
  dataTestId?: string;
  disabled?: boolean;
  style?: React.CSSProperties;
  className?: string;
  children?: React.ReactNode;
  icon?: React.ReactNode;
  target?: React.HTMLAttributeAnchorTarget;
  iconPosition?: "left" | "right";
  id?: string;
  dataEvent?: string;
  onMouseLeave?: React.MouseEventHandler<HTMLAnchorElement>;
  title?: string;
}

const RemiLink = ({
  dataTestId,
  className,
  style,
  children,
  target,
  variant = "accent",
  size = "small",
  icon,
  disabled,
  iconPosition = "left",
  id,
  dataEvent,
  title,
  ...props
}: RemiLinkProps) => {
  return disabled === true ? (
    <div
      style={style}
      title={title}
      className={cn(shallowLinkVariants({ variant, size }), className)}
      data-testid={dataTestId}
    >
      {isTruthy(icon) && icon}
      {children}
    </div>
  ) : (
    <Link
      {...props}
      shallow={true}
      style={style}
      target={target}
      title={title}
      className={cn(shallowLinkVariants({ variant, size }), className)}
      prefetch={false}
      id={id}
      data-testid={dataTestId}
      data-event={dataEvent}
    >
      {isTruthy(icon) && iconPosition === "left" && icon}
      {children}
      {isTruthy(icon) && iconPosition === "right" && icon}
    </Link>
  );
};

export { RemiLink };
